/* eslint-disable camelcase */
// See more details in the Aspen Logging Service documentation: https://roku.atlassian.net/wiki/spaces/IOT/pages/121150677/Cloud+Logging
import dayjs from 'dayjs';

import { QueryStatesPayload } from 'types/aspen/socketMessages';

type LogIds =
  | 'iot.web.load'
  | 'iot.web.unload'
  | 'iot.web.stream.start'
  | 'iot.web.stream.failed'
  | 'iot.web.stream.end'
  | 'iot.web.stream.action'
  | 'iot.web.stream.interaction'
  | 'iot.web.interaction'
  | 'iot.web.event.action'
  | 'iot.web.event.interaction'
  | 'iot.web.event.filter'
  | 'iot.web.event.query.request'
  | 'iot.web.event.query.response'
  | 'iot.web.event.video.request'
  | 'iot.web.event.video.response'
  | 'iot.web.event.video.play'
  | 'iot.web.event.video.error'
  | 'iot.web.event.video.download'
  | 'iot.web.event.video.download.error'
  | 'iot.web.activity.image.request'
  | 'iot.web.activity.image.response';

type BasePayload = {
  account_id: string;
  web_session_id: string;
};

// Core payload types without BasePayload - these are internal definitions
type WebLoadPayload = {
  referrer: string; // The referrer of the page
  user_agent: string; // The user agent of the browser
};

type WebUnloadPayload = Record<string, never>; // Empty object - only BasePayload when enhanced

type WebStreamStartPayload = {
  conn_time_ms: number | null; // The amount of time spent trying to connect to the camera after receiving a signaling server response
  device_id: string; // The virtual device id of the camera being streamed
  ice_gathering_time_ms: number | null; // The amount of time spent gathering ice candidates
  signaling_server_time_ms: number | null; // The amount of time spent waiting for a response from the signaling server
  trigger?: ConnectionTrigger; // The trigger that initiated the connection
  url: string; // The url of the page the streaming occurred
  webrtc_session_id?: string; // The webrtc session id, present in the signaling server response
};

type WebStreamFailedPayload = {
  device_id: string; // The virtual device id of the camera being streamed
  ice_gathering_time_ms: number | null; // The amount of time spent gathering ice candidates
  signaling_server_time_ms: number | null; // The amount of time spent waiting for a response from the signaling server
  webrtc_session_id?: string; // The webrtc session id, present in the signaling server response
  conn_time_ms: number | null; // The amount of time spent trying to connect to the camera after receiving a signaling server response
  trigger?: ConnectionTrigger; // The trigger that initiated the connection
  error?: string; // String of the error
  url: string; // The url of the page the streaming occurred
};

type WebStreamEndPayload = {
  conn_time_ms?: number; // The amount of time spent trying to connect to the camera after receiving a signaling server response
  device_id: string; // The virtual device id of the camera being streamed
  duration_ms?: number | null; // Total time spent streaming the camera
  url: string; // The url of the page the streaming occurred
  webrtc_session_id?: string; // The webrtc session id, present in the signaling server response
  disconnection_count?: number; // Number of disconnections that occurred during the session
};

type WebStreamActionPayload = {
  device_id: string; // The virtual device id of the camera (empty string for multi-camera actions like grid_view)
  action: 'grid_view' | 'focused_view' | 'focused_view_change';
};

type WebStreamInteractionPayload = {
  device_id: string; // The virtual device id of the camera being streamed
  interaction:
    | 'pip'
    | 'sound'
    | 'record'
    | 'speak'
    | 'photo'
    | 'motion_tracking'
    | 'night_vision'
    | 'siren'
    | 'power'
    | 'fullscreen';
};

type WebInteractionPayload = {
  device_id?: string; // Optional device id for context (may be empty for UI interactions)
  interaction: 'live_view' | 'events_view' | 'lights_view';
};

type WebEventActionPayload = {
  action: 'filter_open' | 'filter_close';
};

type WebEventInteractionPayload = {
  event_id: string; // The current event (source)
  target_event_id?: string; // The target event for navigation interactions (next/previous)
  interaction:
    | 'download'
    | 'delete'
    | 'next_event'
    | 'previous_event'
    | 'seek'
    | 'sound'
    | 'fullscreen'
    | 'livestream'
    | 'close'
    | 'play'
    | 'pause'
    | 'replay'
    | 'open';
};

type WebEventFilterPayload = {
  action: 'apply' | 'reset';
  filter_payload?: Omit<QueryStatesPayload, 'nextPageToken'>; // Use existing QueryStatesPayload type
};

// Base payload types for request/response patterns
type BaseRequestPayload = {
  request_id: string;
  query_type: string;
  query_payload: object;
};

type BaseResponsePayload = {
  request_id: string;
  success: boolean;
  has_results?: boolean;
  error?: string;
};

// Event-specific request/response payload types
type WebEventQueryRequestPayload = BaseRequestPayload;
type WebEventQueryResponsePayload = BaseResponsePayload;
type WebEventDeleteRequestPayload = BaseRequestPayload;
type WebEventDeleteResponsePayload = BaseResponsePayload;

type WebEventVideoRequestPayload = {
  request_id: string;
  device_id?: string;
  event_id: string;
};

type WebEventVideoResponsePayload = {
  request_id: string;
  success: boolean;
  has_video?: boolean;
  error?: string;
};

type WebEventVideoPlayPayload = {
  device_id?: string;
  event_id: string;
};

type WebEventVideoErrorPayload = {
  device_id?: string;
  event_id: string;
  error: string; // Error message
};

type WebEventVideoDownloadPayload = {
  device_name?: string; // The device name for context
  download_method?: string; // Method used (server_proxy, server_dash_lightweight, server_ffmpeg_rotation)
  rotation_degrees?: number; // Video rotation applied
};

type WebEventVideoDownloadErrorPayload = {
  device_name?: string; // The device name for context
  error: string; // Error message
};

type WebActivityImageRequestPayload = BaseRequestPayload;
type WebActivityImageResponsePayload = BaseResponsePayload;

// Public API for components - BasePayload is automatically added by useAspenLogger hook
export type LogAspenEventParams =
  | { log_id: 'iot.web.load'; payload: WebLoadPayload }
  | { log_id: 'iot.web.unload'; payload: WebUnloadPayload }
  | { log_id: 'iot.web.stream.start'; payload: WebStreamStartPayload }
  | { log_id: 'iot.web.stream.failed'; payload: WebStreamFailedPayload }
  | { log_id: 'iot.web.stream.end'; payload: WebStreamEndPayload }
  | { log_id: 'iot.web.stream.action'; payload: WebStreamActionPayload }
  | { log_id: 'iot.web.stream.interaction'; payload: WebStreamInteractionPayload }
  | { log_id: 'iot.web.interaction'; payload: WebInteractionPayload }
  | { log_id: 'iot.web.event.action'; payload: WebEventActionPayload }
  | { log_id: 'iot.web.event.interaction'; payload: WebEventInteractionPayload }
  | { log_id: 'iot.web.event.filter'; payload: WebEventFilterPayload }
  | { log_id: 'iot.web.event.query.request'; payload: WebEventQueryRequestPayload }
  | { log_id: 'iot.web.event.query.response'; payload: WebEventQueryResponsePayload }
  | { log_id: 'iot.web.event.delete.request'; payload: WebEventDeleteRequestPayload }
  | { log_id: 'iot.web.event.delete.response'; payload: WebEventDeleteResponsePayload }
  | { log_id: 'iot.web.event.video.request'; payload: WebEventVideoRequestPayload }
  | { log_id: 'iot.web.event.video.response'; payload: WebEventVideoResponsePayload }
  | { log_id: 'iot.web.event.video.play'; payload: WebEventVideoPlayPayload }
  | { log_id: 'iot.web.event.video.error'; payload: WebEventVideoErrorPayload }
  | { log_id: 'iot.web.event.video.download'; payload: WebEventVideoDownloadPayload }
  | { log_id: 'iot.web.event.video.download.error'; payload: WebEventVideoDownloadErrorPayload }
  | { log_id: 'iot.web.activity.image.request'; payload: WebActivityImageRequestPayload }
  | { log_id: 'iot.web.activity.image.response'; payload: WebActivityImageResponsePayload };

// Utility type to add BasePayload to any payload type
type WithBasePayload<T> = BasePayload & T;

// Dynamic type that automatically adds BasePayload to all LogAspenEventParams payloads
type AspenLogType = WithBasePayload<LogAspenEventParams['payload']>;

interface SendAspenLogParams {
  endpoint: string;
  log_id: LogIds;
  timestamp: number;
  service?: string;
  request_id?: string;
  payload: AspenLogType;
}

/**
 * Sends a log to the Aspen logging service.
 *
 * @param endpoint - The endpoint to send the log to.
 * @param log_id - Specific log id, see LogIds.
 * @param timestamp - Unix timestamp when the event occurred (in seconds, NOT milliseconds like JS does).
 * @param service - Identifies the sender of the log, can be static for the web app.
 * @param request_id - Request ID when relevant (e.g., from signaling server response).
 * @param payload - Varies depending on the log id, see LogIds for details.
 * @returns A promise that resolves when the log has been sent.
 */
export const sendAspenLog = ({
  endpoint,
  log_id,
  timestamp,
  request_id,
  payload,
  service = 'smarthome-webapp',
}: SendAspenLogParams): void => {
  const aspenLogsEndpoint = `${endpoint}/async/v1/iot_cloud`;

  const payloadJSON = JSON.stringify(payload);

  navigator.sendBeacon(
    aspenLogsEndpoint,
    JSON.stringify({
      log_id,
      timestamp,
      request_id,
      payload: payloadJSON,
      service,
    }),
  );
};

/**
 * Log an event to the Aspen logging service - automatically adds BasePayload.
 *
 * @param endpoint - The endpoint to send the log to.
 * @param basePayload - The base payload (account_id, web_session_id) to add to all logs
 */
export const logAspenEvent =
  (endpoint: string, basePayload: BasePayload) =>
  ({ log_id, payload }: LogAspenEventParams): void => {
    const enhancedPayload = {
      ...basePayload,
      ...payload,
    } as AspenLogType;

    sendAspenLog({
      endpoint,
      log_id,
      timestamp: dayjs().unix(),
      payload: enhancedPayload,
    });
  };
